/**
 * Comparison of inverters and circuits in one window (§4.1, acceptance 5).
 *
 * Both views are offered: absolute kW and normalised per installed kWp, because
 * inverters of different size are not comparable in absolute terms. When the
 * capacity of a device is unknown, the normalised view says so instead of
 * silently showing absolute numbers.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { Resolution, Series } from '../api/types';
import { Notice } from '../components/Notice';
import { SeriesView } from '../components/SeriesView';
import { MAX_SERIES } from '../components/palette';
import { TimeRange, rangeBounds, type RangeKey } from '../components/TimeRange';
import { useApi } from '../lib/useApi';

export function ComparePage() {
  const { t } = useTranslation();
  const plants = useApi(() => api.plants(), []);
  const [plantId, setPlantId] = useState<string>('');
  const effectivePlantId = plantId || plants.data?.[0]?.id || '';
  const devices = useApi(
    () => (effectivePlantId ? api.devices(effectivePlantId, 'inverter') : Promise.resolve([])),
    [effectivePlantId],
  );

  const [selected, setSelected] = useState<string[]>([]);
  const [range, setRange] = useState<RangeKey>('today');
  const [resolution, setResolution] = useState<Resolution>('15min');
  const [normalize, setNormalize] = useState(true);
  const [result, setResult] = useState<Series[] | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  async function run() {
    setErrorCode(null);
    try {
      const series = await api.compare({
        subjects: selected.map((id) => ({ type: 'device' as const, id })),
        ...rangeBounds(range),
        resolution,
        metrics: ['pac_w'],
        normalize,
      });
      setResult(series);
    } catch (err) {
      setErrorCode(err instanceof Error ? (err as { code?: string }).code ?? 'server.error' : 'server.error');
    }
  }

  const capacityUnknown = result?.some((series) => series.notes.includes('capacity_unknown'));
  const normalised = Boolean(result?.[0]?.normalized);

  return (
    <>
      <h1>{t('compare.title')}</h1>
      <p className="muted">{t('compare.hint')}</p>

      <div className="toolbar">
        <label className="field field--inline">
          <span>{t('nav.plants')}</span>
          <select
            value={effectivePlantId}
            onChange={(event) => {
              setPlantId(event.target.value);
              setSelected([]);
              setResult(null);
            }}
          >
            {(plants.data ?? []).map((plant) => (
              <option key={plant.id} value={plant.id}>
                {plant.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field field--inline">
          <input
            type="checkbox"
            checked={normalize}
            onChange={(event) => setNormalize(event.target.checked)}
          />
          <span>{t('compare.normalize')}</span>
        </label>
      </div>

      <TimeRange
        range={range}
        resolution={resolution}
        onRangeChange={setRange}
        onResolutionChange={setResolution}
      />

      <fieldset className="picker">
        <legend>{t('compare.selectDevices')}</legend>
        <div className="picker__items">
          {(devices.data ?? []).map((device) => {
            const checked = selected.includes(device.id);
            const atLimit = !checked && selected.length >= MAX_SERIES;
            return (
              <label key={device.id} className={atLimit ? 'is-disabled' : ''}>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={atLimit}
                  onChange={(event) =>
                    setSelected((current) =>
                      event.target.checked
                        ? [...current, device.id]
                        : current.filter((id) => id !== device.id),
                    )
                  }
                />
                <span>{device.name}</span>
              </label>
            );
          })}
        </div>
        <button
          type="button"
          className="button"
          disabled={selected.length === 0}
          onClick={() => void run()}
        >
          {t('compare.run')}
        </button>
      </fieldset>

      <Notice tone="error" code={errorCode} />
      {capacityUnknown ? <Notice tone="warning">{t('compare.capacityUnknown')}</Notice> : null}

      {result && result.length > 0 ? (
        <SeriesView
          series={result}
          metric="pac_w"
          title={normalised ? t('chart.normalized') : t('chart.power')}
          axisLabel={normalised ? 'W/Wp' : t('chart.power')}
          scale={normalised ? 1 : 1000}
          digits={normalised ? 3 : 1}
        />
      ) : null}
    </>
  );
}
